import React, { useCallback, useMemo, useRef, useState } from "react";
import { Upload, X, FileText } from "lucide-react";
import {
  useImportCapabilities,
  formatBytes,
  formatCount,
} from "../hooks/useImportCapabilities";

export type SelectedFile = {
  file: File;
  name: string;
  size: number;
};

function endsWithAny(name: string, exts: string[]): boolean {
  const n = name.toLowerCase();
  return exts.some((e) => n.endsWith(e));
}

function mimeMatches(type: string, mimes: string[]): boolean {
  if (!type) return false;
  const t = type.toLowerCase();
  return mimes.some((m) => t === m.toLowerCase());
}

/**
 * Drag-and-drop + click-to-pick CSV picker. Client-side sanity checks
 * (extension / mime / byte size) are driven by GET /api/import/capabilities
 * so this component never hard-codes upload limits. The server upload
 * endpoint remains the sole authority — a file that passes here may still
 * be rejected server-side and the resulting error is surfaced by the
 * parent step.
 */
export function FileDropzone({
  selected,
  onSelect,
  onClear,
  disabled,
  busy,
}: {
  selected: SelectedFile | null;
  onSelect: (f: SelectedFile) => void;
  onClear: () => void;
  disabled?: boolean;
  busy?: boolean;
}) {
  const { caps } = useImportCapabilities();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accept = useMemo(
    () => [...caps.accepted_file_extensions, ...caps.accepted_mime_types].join(","),
    [caps.accepted_file_extensions, caps.accepted_mime_types],
  );

  const validateAndSelect = useCallback(
    (f: File) => {
      setError(null);
      const extOk = endsWithAny(f.name || "", caps.accepted_file_extensions);
      const mimeOk = mimeMatches(f.type || "", caps.accepted_mime_types);
      if (!extOk && !mimeOk) {
        const exts = caps.accepted_file_extensions.map((e) => e.toUpperCase().replace(".", "")).join(", ");
        setError(`Only ${exts} files are supported.`);
        return;
      }
      if (f.size === 0) {
        setError("The file is empty.");
        return;
      }
      if (f.size > caps.max_file_bytes) {
        setError(
          `File is too large (${formatBytes(f.size)}). Max is ${formatBytes(caps.max_file_bytes)}.`,
        );
        return;
      }
      onSelect({ file: f, name: f.name, size: f.size });
    },
    [caps, onSelect],
  );

  const openPicker = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    if (disabled) return;
    const f = e.dataTransfer.files?.[0];
    if (f) validateAndSelect(f);
  };

  if (selected) {
    return (
      <div
        className="rounded-md border border-slate-200 bg-white p-4 flex items-center gap-3"
        data-testid="file-selected"
      >
        <div className="w-10 h-10 rounded-md bg-slate-100 grid place-items-center shrink-0">
          <FileText className="w-5 h-5 text-slate-600" />
        </div>
        <div className="min-w-0 flex-1">
          <div
            className="text-sm font-medium truncate"
            data-testid="file-selected-name"
            title={selected.name}
          >
            {selected.name}
          </div>
          <div className="text-xs text-slate-500">
            {formatBytes(selected.size)} · {caps.accepted_file_extensions[0]?.replace(".", "").toUpperCase() || "CSV"}
          </div>
        </div>
        <button
          type="button"
          onClick={onClear}
          disabled={busy || disabled}
          className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 disabled:opacity-40"
          data-testid="file-clear-btn"
        >
          <X className="w-3.5 h-3.5" /> Replace
        </button>
      </div>
    );
  }

  return (
    <div>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-label="Upload CSV file"
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openPicker();
          }
        }}
        onClick={openPicker}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        className={`rounded-md border-2 border-dashed p-8 text-center transition-colors motion-reduce:transition-none cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        } ${
          dragActive
            ? "border-slate-900 bg-slate-50"
            : "border-slate-300 bg-white hover:border-slate-500"
        }`}
        data-testid="file-dropzone"
        data-drag-active={dragActive ? "true" : "false"}
      >
        <Upload className="w-6 h-6 mx-auto text-slate-500" />
        <div className="mt-2 text-sm font-medium text-slate-800">
          Drag a CSV file here, or click to browse
        </div>
        <div className="mt-1 text-xs text-slate-500" data-testid="dropzone-limits-hint">
          CSV · UTF-8 · up to {formatBytes(caps.max_file_bytes)} · {formatCount(caps.max_rows)} rows
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          data-testid="file-input"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) validateAndSelect(f);
            e.target.value = "";
          }}
        />
      </div>
      {error && (
        <div
          role="alert"
          className="mt-2 text-sm text-rose-700 flex items-center gap-1.5"
          data-testid="file-error"
        >
          {error}
        </div>
      )}
    </div>
  );
}
